import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, FileVideo, History, ListVideo, RefreshCw, RotateCcw, Search, Trash2, Tv2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient, type DeletionHistoryEntry, type DeletionHistoryFilter, type DeletionHistoryPage } from '../lib/apiClient';
import { AIRECORDING_VIDEOS_REFRESH_EVENT } from '../hooks/useVideos';

export const formatSize = (bytes: number) =>
  bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

// Hover, focus and the loading shimmer (inline styles can't express them). Shared with the Recycle Bin.
export const BIN_STYLES = `
.dh-row { transition: background-color .15s ease; }
.dh-row:hover { background: color-mix(in srgb, var(--theme-primary, #4f46e5) 5%, transparent); }
.dh-search:focus-within { border-color: var(--theme-primary, #4f46e5); box-shadow: 0 0 0 3px color-mix(in srgb, var(--theme-primary, #4f46e5) 15%, transparent); }
@keyframes dh-shimmer { 0%, 100% { opacity: .45 } 50% { opacity: .9 } }
.dh-skeleton { animation: dh-shimmer 1.4s ease-in-out infinite; background: var(--theme-border, #e5e7eb); border-radius: 6px; }
.dh-chip:hover:not([aria-pressed="true"]), .dh-page:hover:not(:disabled):not([aria-current="page"]) { background: color-mix(in srgb, var(--theme-primary, #4f46e5) 7%, transparent); }
.dh-chip:focus-visible, .dh-page:focus-visible { outline: 2px solid var(--theme-primary, #4f46e5); outline-offset: 2px; }
`;

export const muted = 'var(--text-secondary, #6b7280)';
export const border = 'var(--theme-border, #e5e7eb)';

const Chip = ({ icon, label, title }: { icon: ReactNode; label: string; title?: string }) => (
  <span title={title ?? label} style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 200,
    padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500,
    background: 'color-mix(in srgb, var(--theme-primary, #4f46e5) 9%, transparent)',
    color: 'var(--primary-text, #4f46e5)',
  }}>
    {icon}
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
  </span>
);

export const Centered = ({ children }: { children: ReactNode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '48px 16px', gap: 10 }}>
    {children}
  </div>
);

export const Badge = ({ children }: { children: ReactNode }) => (
  <span aria-hidden style={{
    width: 52, height: 52, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'color-mix(in srgb, var(--theme-primary, #4f46e5) 10%, transparent)', color: 'var(--primary-text, #4f46e5)',
  }}>{children}</span>
);

export const RetryButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, padding: '7px 14px', borderRadius: 8,
      fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
      background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
    }}>
      <RefreshCw size={14} aria-hidden /> {t('common.retry', 'Retry')}
    </button>
  );
};

export const SearchBox = ({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) => (
  <label className="dh-search" style={{
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', minWidth: 0,
    border: `1px solid ${border}`, borderRadius: 10, transition: 'border-color .15s, box-shadow .15s',
    background: 'var(--card-bg, transparent)',
  }}>
    <Search size={15} aria-hidden style={{ color: muted, flexShrink: 0 }} />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'inherit', fontSize: 13 }}
    />
  </label>
);

/** What each entry says happened (moved to the bin, restored, deleted permanently…). */
const ACTION_TEXT: Record<string, [string, string]> = {
  deleted: ['Deleted by you', 'Deleted by {{email}}'],
  moved_to_bin: ['Moved to the Recycle bin by you', 'Moved to the Recycle bin by {{email}}'],
  restored: ['Restored by you', 'Restored by {{email}}'],
  deleted_permanently: ['Deleted permanently by you', 'Deleted permanently by {{email}}'],
  purged: ['Deleted permanently after 30 days in the Recycle bin', 'Deleted permanently after 30 days in the Recycle bin'],
};

/** Icon and colour of each kind of entry (moved to the bin, restored, deleted for good). */
const actionLook = (action: string) =>
  action === 'moved_to_bin' ? { Icon: Trash2, color: 'var(--theme-warning, #d97706)' }
    : action === 'restored' || action === 'restored_by_admin' ? { Icon: RotateCcw, color: 'var(--theme-success, #16a34a)' }
    : { Icon: Trash2, color: 'var(--theme-error, #dc2626)' };

const PAGE_SIZE = 25;
const PERIODS = [0, 7, 30, 90] as const;
const FILTERS: [DeletionHistoryFilter, string][] = [
  ['all', 'All'],
  ['moved_to_bin', 'Moved to bin'],
  ['restored', 'Restored'],
  ['deleted_permanently', 'Deleted permanently'],
];

/** Page numbers to show: first, last and the ones around the current page; null = a gap (…). */
const pageList = (page: number, pages: number): (number | null)[] => {
  const keep = [...new Set([1, page - 1, page, page + 1, pages])].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  keep.forEach((p, i) => {
    if (i > 0 && p - keep[i - 1] > 1) out.push(p - keep[i - 1] === 2 ? p - 1 : null);
    out.push(p);
  });
  return out;
};

/**
 * History tab of the Recycle Bin: every move to the bin, restore and permanent deletion of the
 * user's videos. Searched, filtered and paged on the server (the whole year of history, not only
 * the latest entries); each page grouped by day. The full record (files, comments…) stays in the database.
 */
export default function DeletionHistory() {
  const { t } = useTranslation();
  const [data, setData] = useState<DeletionHistoryPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState(''); // the query, applied after typing pauses
  const [filter, setFilter] = useState<DeletionHistoryFilter>('all');
  const [days, setDays] = useState<number>(0);
  const [page, setPage] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);
  // Only the latest request is applied (reloads in quick succession must not show an older answer).
  const seq = useRef(0);

  // Search after typing pauses; a new search, filter or period starts again at page 1.
  useEffect(() => {
    const next = query.trim();
    if (next === search) return;
    const id = window.setTimeout(() => {
      setSearch(next);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [query, search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    const { data: result, error } = await apiClient.getDeletionHistory({ page, pageSize: PAGE_SIZE, q: search, filter, days });
    if (mine !== seq.current) return;
    setLoading(false);
    // Not a page (e.g. a server without paging): shown as a failed load, not a broken tab.
    const ok = !error && !!result && Array.isArray(result.items);
    setFailed(!ok);
    if (ok) {
      // A page past the end (entries removed meanwhile): go to the last one.
      const pages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
      if (page > pages) setPage(pages);
      else setData(result);
    }
  }, [page, search, filter, days]);
  // Bin actions elsewhere (Undo, restore, delete) add entries: reload with the lists.
  useEffect(() => {
    void load();
    const onRefresh = () => void load();
    window.addEventListener(AIRECORDING_VIDEOS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(AIRECORDING_VIDEOS_REFRESH_EVENT, onRefresh);
  }, [load]);

  // The page's entries, grouped by day (newest first, as the list comes).
  const groups = useMemo(() => {
    const today = new Date();
    const yesterday = new Date(Date.now() - 86400000);
    const out: { label: string; items: DeletionHistoryEntry[] }[] = [];
    for (const e of data?.items ?? []) {
      const d = new Date(e.createdAt);
      const label = dayKey(d) === dayKey(today) ? t('deletion_history.today', 'Today')
        : dayKey(d) === dayKey(yesterday) ? t('deletion_history.yesterday', 'Yesterday')
        : d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
      const last = out[out.length - 1];
      if (last?.label === label) last.items.push(e);
      else out.push({ label, items: [e] });
    }
    return out;
  }, [data, t]);

  const filtered = !!search || filter !== 'all' || days > 0;
  const clearFilters = () => {
    setQuery('');
    setSearch('');
    setFilter('all');
    setDays(0);
    setPage(1);
  };
  const goTo = (p: number) => {
    setPage(p);
    topRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const renderRow = (e: DeletionHistoryEntry) => {
    const d = e.details ?? {};
    const byOwner = e.actorEmail === e.ownerEmail;
    // An admin's restore reads as a restore by them.
    const action = e.action === 'restored_by_admin' ? 'restored' : e.action;
    const [you, by] = ACTION_TEXT[action] ?? ACTION_TEXT.deleted;
    const { Icon, color } = actionLook(e.action);
    // Channel / playlists it was removed from: only for deletions that removed it for good.
    const removedFrom = action !== 'moved_to_bin' && action !== 'restored';
    const playlists = removedFrom ? (d.playlists ?? []).map((p) => p.title || t('deletion_history.unnamed_playlist', 'Playlist')) : [];
    const channel = removedFrom ? d.channel : null;
    return (
      <li key={e.id} className="dh-row" style={{ display: 'flex', gap: 12, padding: '12px 10px', borderRadius: 10, alignItems: 'flex-start' }}>
        {/* What the entry is about (a video), like a file-type icon — neutral, not an action. */}
        <span aria-hidden style={{
          flexShrink: 0, width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'color-mix(in srgb, var(--text-secondary, #6b7280) 10%, transparent)', color: muted,
        }}>
          <FileVideo size={18} strokeWidth={1.75} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {e.videoTitle || t('deletion_history.untitled', 'Untitled video')}
            </span>
            {d.notRemoved?.length ? (
              <span title={t('deletion_history.not_removed', 'Part of the clean-up did not finish; support can complete it.')}
                style={{ display: 'inline-flex', color: 'var(--theme-warning, #d97706)', flexShrink: 0 }}>
                <AlertTriangle size={14} aria-label={t('deletion_history.not_removed', 'Part of the clean-up did not finish; support can complete it.')} />
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', fontSize: 12, color: muted, marginTop: 2 }}>
            <span>{new Date(e.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
            <span aria-hidden>·</span>
            <Icon size={12} aria-hidden style={{ color, flexShrink: 0 }} />
            <span style={action === 'restored' ? { color } : undefined}>
              {byOwner
                ? t(`deletion_history.action_${action}_you`, you)
                : t(`deletion_history.action_${action}_by`, by, { email: e.actorEmail })}
            </span>
          </div>
          {(channel || playlists.length > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {channel && (
                <Chip icon={<Tv2 size={11} aria-hidden />} label={channel.name || t('deletion_history.channel', 'Channel')}
                  title={t('deletion_history.removed_from_channel', 'Removed from channel {{name}}', { name: channel.name || '', interpolation: { escapeValue: false } })} />
              )}
              {playlists.slice(0, 2).map((name, i) => (
                <Chip key={i} icon={<ListVideo size={11} aria-hidden />} label={name}
                  title={t('deletion_history.removed_from_playlist', 'Removed from playlist {{name}}', { name, interpolation: { escapeValue: false } })} />
              ))}
              {playlists.length > 2 && (
                <Chip icon={<ListVideo size={11} aria-hidden />} label={`+${playlists.length - 2}`} title={playlists.slice(2).join(', ')} />
              )}
            </div>
          )}
        </div>
      </li>
    );
  };

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageButton = (active: boolean): React.CSSProperties => ({
    minWidth: 32, height: 32, padding: '0 8px', borderRadius: 8, fontSize: 13, fontWeight: active ? 700 : 500,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--theme-primary, #4f46e5)' : border}`,
    background: active ? 'var(--theme-primary, #4f46e5)' : 'transparent',
    color: active ? 'var(--button-primary-foreground, #fff)' : 'inherit',
  });

  // Nothing recorded at all: no toolbar, just the empty state.
  const nothingYet = data && data.counts.all === 0 && !filtered;

  return (
    <div ref={topRef} style={{ scrollMarginTop: 16 }}>
      <style>{BIN_STYLES}</style>
      {data && !nothingYet && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 6 }}>
          {/* Search and period */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', maxWidth: 320 }}>
              <SearchBox value={query} onChange={setQuery} placeholder={t('deletion_history.search', 'Search by title')} />
            </div>
            <div style={{ position: 'relative' }}>
              <select
                value={days}
                onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }}
                aria-label={t('deletion_history.period_label', 'Period')}
                style={{
                  appearance: 'none', padding: '7px 30px 7px 12px', borderRadius: 10, fontSize: 13, cursor: 'pointer',
                  border: `1px solid ${border}`, background: 'var(--dropdown-bg, var(--card-bg, transparent))', color: 'inherit',
                }}
              >
                {PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {p === 0 ? t('deletion_history.period_all', 'Any time') : t(`deletion_history.period_${p}`, `Last ${p} days`)}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} aria-hidden style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: muted }} />
            </div>
          </div>
          {/* What happened: filter chips with their counts (under the search and period) */}
          <div role="group" aria-label={t('deletion_history.filter_label', 'Show')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map(([id, label]) => {
              const active = filter === id;
              return (
                <button
                  key={id}
                  type="button"
                  className="dh-chip"
                  aria-pressed={active}
                  onClick={() => { setFilter(id); setPage(1); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background-color .15s',
                    border: `1px solid ${active ? 'var(--theme-primary, #4f46e5)' : border}`,
                    background: active ? 'color-mix(in srgb, var(--theme-primary, #4f46e5) 12%, transparent)' : 'transparent',
                    color: active ? 'var(--primary-text, #4f46e5)' : 'inherit',
                  }}
                >
                  {t(`deletion_history.filter_${id}`, label)}
                  <span style={{ fontSize: 11, fontWeight: 600, color: active ? 'inherit' : muted }}>{data.counts[id]}</span>
                </button>
              );
            })}
          </div>
          {/* Where we are in the results */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: muted, minHeight: 22 }}>
            {total > 0 && (
              <span aria-live="polite">
                {t('deletion_history.showing', 'Showing {{from}}–{{to}} of {{total}}', {
                  from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total,
                })}
              </span>
            )}
            {failed && (
              <span role="alert" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--theme-error, #dc2626)' }}>
                <AlertTriangle size={13} aria-hidden /> {t('deletion_history.load_failed', 'Could not load the deletion history.')}
                <button type="button" onClick={() => void load()} style={{
                  padding: 0, border: 'none', background: 'transparent', color: 'inherit', fontSize: 12, fontWeight: 600,
                  textDecoration: 'underline', cursor: 'pointer',
                }}>{t('common.retry', 'Retry')}</button>
              </span>
            )}
            {filtered && (
              <button type="button" onClick={clearFilters} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, border: 'none', background: 'transparent',
                color: 'var(--primary-text, #4f46e5)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                <X size={13} aria-hidden /> {t('deletion_history.clear_filters', 'Clear filters')}
              </button>
            )}
          </div>
        </div>
      )}

      {failed && !data ? (
        <Centered>
          <Badge><AlertTriangle size={22} /></Badge>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('deletion_history.load_failed', 'Could not load the deletion history.')}</p>
          <RetryButton onClick={() => void load()} />
        </Centered>
      ) : !data ? (
        <ul aria-busy="true" aria-label={t('loading', 'Loading…')} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {[0, 1, 2].map((i) => (
            <li key={i} style={{ display: 'flex', gap: 12, padding: '12px 10px' }}>
              <span className="dh-skeleton" style={{ width: 36, height: 36, borderRadius: 10 }} />
              <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
                <span className="dh-skeleton" style={{ height: 12, width: `${60 - i * 12}%` }} />
                <span className="dh-skeleton" style={{ height: 10, width: '35%' }} />
              </span>
            </li>
          ))}
        </ul>
      ) : nothingYet ? (
        <Centered>
          <Badge><History size={22} /></Badge>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t('deletion_history.empty_title', 'Nothing deleted yet')}</p>
          <p style={{ margin: 0, fontSize: 13, color: muted, maxWidth: 360 }}>
            {t('deletion_history.empty_hint', 'Every video you delete, restore or delete permanently is listed here.')}
          </p>
        </Centered>
      ) : total === 0 ? (
        <Centered>
          <p style={{ margin: 0, fontSize: 13, color: muted }}>{t('deletion_history.no_matches', 'No history entries match your search or filters.')}</p>
        </Centered>
      ) : (
        // The current page stays visible (dimmed) while the next one loads.
        <div aria-busy={loading} style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          {groups.map((g) => (
            <section key={g.label} aria-label={g.label}>
              <h3 style={{ margin: '10px 10px 2px', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: muted }}>
                {g.label}
              </h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{g.items.map(renderRow)}</ul>
            </section>
          ))}
        </div>
      )}

      {data && pages > 1 && (
        <nav aria-label={t('deletion_history.pagination', 'Pages')} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap',
          marginTop: 12, paddingTop: 12, borderTop: `1px solid ${border}`,
        }}>
          <button type="button" className="dh-page" disabled={page <= 1 || loading} onClick={() => goTo(page - 1)}
            style={{ ...pageButton(false), opacity: page <= 1 ? 0.45 : 1, cursor: page <= 1 ? 'default' : 'pointer' }}>
            <ChevronLeft size={15} aria-hidden /> {t('deletion_history.prev', 'Previous')}
          </button>
          {pageList(page, pages).map((p, i) => p === null ? (
            <span key={`gap${i}`} aria-hidden style={{ color: muted, padding: '0 2px' }}>…</span>
          ) : (
            <button key={p} type="button" className="dh-page" aria-current={p === page ? 'page' : undefined}
              disabled={loading && p !== page} onClick={() => p !== page && goTo(p)} style={pageButton(p === page)}>
              {p}
            </button>
          ))}
          <button type="button" className="dh-page" disabled={page >= pages || loading} onClick={() => goTo(page + 1)}
            style={{ ...pageButton(false), opacity: page >= pages ? 0.45 : 1, cursor: page >= pages ? 'default' : 'pointer' }}>
            {t('deletion_history.next', 'Next')} <ChevronRight size={15} aria-hidden />
          </button>
        </nav>
      )}
    </div>
  );
}
